import { useParams, Link, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { VideoPlayer } from '../components/VideoPlayer';
import { MediaCarousel } from '../components/MediaCarousel';
import { Lightbox } from '../components/Lightbox';
import { PostButtons } from '../components/PostButtons';
import { RedesignSubmissions } from '../components/RedesignSubmissions';
import { RichTextView } from '../components/RichText';
import { Halftone } from '../components/Halftone';
import { Avatar } from '../components/Avatar';
import { FoundingBadge } from '../components/FoundingBadge';
import { Skeleton } from '../components/Skeleton';
import { WatchButton, HeartIcon } from '../components/WatchButton';
import { Disclosure } from '../components/Disclosure';
import { EmptyState } from '../components/EmptyState';
import { Badge, Button, Heading, Field, Select } from '../components/ui';
import { useSession } from '../lib/session';
import {
  fetchPostBySlug,
  fetchComments,
  fetchViewerLikedPostIds,
  fetchViewerLikedCommentIds,
  fetchPollOptionsForPost,
  fetchAuctionForPost,
  fetchRecentBids,
  togglePostLike,
  toggleCommentLike,
  togglePollVote,
  toggleEventRsvp,
  placeBid,
  fetchMyPaymentMethod,
  fetchPostageQuote,
  fetchMyAddresses,
  fetchLotShippingAddressId,
  setLotShippingAddress,
  insertComment,
  deleteComment,
  nestComments,
  type CommentsCursor,
  type PostageQuote,
  type ShippingAddress,
} from '../lib/queries';
import { AddCardModal } from '../components/AddCardModal';
import { BuyNowCheckoutModal } from '../components/BuyNowCheckoutModal';
import type { Post as PostType, Comment, PollOption, PostEvent, PostAuction, AuctionBid, Media } from '../lib/types';
import { buildIcs, downloadIcs } from '../lib/ics';
import { authorHandle, profilePath } from '../lib/display';
import './Post.css';

const RECENT_BIDS_LIMIT = 10;

export default function Post({ layout = 'rail' }: { layout?: 'rail' | 'stacked' } = {}) {
  const { slug = '' } = useParams();
  const { profile } = useSession();
  const location = useLocation();

  const [post, setPost] = useState<PostType | null>(null);
  // Measured width (px) of the centred, width-capped media box, reported up by
  // the carousel, so the floating like/share row can align to the real media
  // edge (portrait slides / odd videos) instead of the full container edge.
  const [mediaW, setMediaW] = useState<number | null>(null);
  // Flat comments - nested into a tree at render time so paginated pages
  // merge cleanly (a reply on page 2 finds its parent from page 1).
  const [flatComments, setFlatComments] = useState<Comment[]>([]);
  const [commentsCursor, setCommentsCursor] = useState<CommentsCursor | null>(null);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [pollOptions, setPollOptions] = useState<PollOption[]>([]);
  const [eventState, setEventState] = useState<PostEvent | null>(null);
  const [auctionState, setAuctionState] = useState<PostAuction | null>(null);
  const [recentBids, setRecentBids] = useState<AuctionBid[]>([]);
  const [bidError, setBidError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Bidding-card gate (Sales Room). null = not yet known; true/false once
  // member_payment_methods is read. Only fetched for auction posts.
  const [cardOnFile, setCardOnFile] = useState<boolean | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  // Live postage estimate for THIS viewer's saved address (067). Null = no
  // estimate possible (no address / lot not rateable / Shippo down) → the
  // AuctionBlock falls back to the lot's flat shipping_cost display.
  const [postageQuote, setPostageQuote] = useState<PostageQuote | null>(null);
  // Address book + the address chosen for THIS lot (per-lot choice, else
  // default). Drives the postage shown and the Buy Now checkout.
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const p = await fetchPostBySlug(slug, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (!p) { setNotFound(true); return; }
        setPost(p);
        setLikeCount(p.like_count ?? 0);
        setCommentCount(p.comment_count ?? 0);
        // fetchPostBySlug already attaches event + auction + viewer state.
        // Hydrate local state directly from those - mirrors how polls used to
        // be re-fetched but skipping the round trip now that the post fetch
        // bundles it for us.
        setEventState(p.event ?? null);
        setAuctionState(p.auction ?? null);

        const page = await fetchComments(p.id, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;

        if (profile) {
          const [postLiked, commentLiked, polls, bids] = await Promise.all([
            fetchViewerLikedPostIds([p.id], profile.id, ctrl.signal),
            fetchViewerLikedCommentIds(page.flat.map((c) => c.id), profile.id, ctrl.signal),
            fetchPollOptionsForPost(p.id, profile.id, ctrl.signal),
            p.auction ? fetchRecentBids(p.id, RECENT_BIDS_LIMIT, ctrl.signal) : Promise.resolve<AuctionBid[]>([]),
          ]);
          if (ctrl.signal.aborted) return;
          setLiked(postLiked.has(p.id));
          setFlatComments(page.flat.map((c) => ({ ...c, viewer_liked: commentLiked.has(c.id) })));
          setPollOptions(polls);
          setRecentBids(bids);
        } else {
          const [polls, bids] = await Promise.all([
            fetchPollOptionsForPost(p.id, null, ctrl.signal),
            p.auction ? fetchRecentBids(p.id, RECENT_BIDS_LIMIT, ctrl.signal) : Promise.resolve<AuctionBid[]>([]),
          ]);
          if (ctrl.signal.aborted) return;
          setFlatComments(page.flat);
          setPollOptions(polls);
          setRecentBids(bids);
        }
        setCommentsCursor(page.nextCursor);
        setHasMoreComments(page.nextCursor !== null);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[post] fetch failed', err);
        setNotFound(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [slug, profile?.id]);

  // Read the viewer's bidding-card state, but only for auction posts and only
  // when signed in. Drives the "Add a card to bid" gate in AuctionBlock; the
  // place_bid RPC enforces it for real regardless.
  const refreshCard = useCallback(() => {
    fetchMyPaymentMethod()
      .then((pm) => setCardOnFile(!!pm?.has_card_on_file))
      .catch(() => setCardOnFile(false));
  }, []);

  useEffect(() => {
    if (!profile || !auctionState) { setCardOnFile(null); return; }
    let cancelled = false;
    fetchMyPaymentMethod()
      .then((pm) => { if (!cancelled) setCardOnFile(!!pm?.has_card_on_file); })
      .catch(() => { if (!cancelled) setCardOnFile(false); });
    return () => { cancelled = true; };
  }, [profile?.id, auctionState?.post_id]);

  // Load the member's address book + the address chosen for this lot (per-lot
  // choice, else default), once they can bid.
  useEffect(() => {
    if (!profile || !auctionState || cardOnFile !== true) {
      setAddresses([]); setSelectedAddressId(null); return;
    }
    let cancelled = false;
    (async () => {
      const [list, chosen] = await Promise.all([
        fetchMyAddresses().catch(() => [] as ShippingAddress[]),
        fetchLotShippingAddressId(auctionState.post_id).catch(() => null),
      ]);
      if (cancelled) return;
      setAddresses(list);
      const fallback = list.find((a) => a.is_default)?.id ?? list[0]?.id ?? null;
      const chosenValid = chosen && list.some((a) => a.id === chosen) ? chosen : null;
      setSelectedAddressId(chosenValid ?? fallback);
    })();
    return () => { cancelled = true; };
  }, [profile?.id, auctionState?.post_id, cardOnFile]);

  // Personal postage estimate for the chosen address. Re-runs when the address
  // changes (or after the register flow flips cardOnFile). Never throws.
  useEffect(() => {
    if (!profile || !auctionState || cardOnFile !== true) { setPostageQuote(null); return; }
    let cancelled = false;
    fetchPostageQuote(auctionState.post_id, selectedAddressId ?? undefined)
      .then((q) => { if (!cancelled) setPostageQuote(q); });
    return () => { cancelled = true; };
  }, [profile?.id, auctionState?.post_id, cardOnFile, selectedAddressId]);

  // Pick a delivery address for this lot: update the live postage + remember it.
  const handleSelectAddress = useCallback(async (addressId: string) => {
    setSelectedAddressId(addressId);
    const pid = auctionState?.post_id;
    if (pid) { try { await setLotShippingAddress(pid, addressId); } catch { /* non-fatal */ } }
  }, [auctionState?.post_id]);

  const loadMoreComments = useCallback(async () => {
    if (loadingMoreComments || !hasMoreComments || !commentsCursor || !post) return;
    setLoadingMoreComments(true);
    const ctrl = new AbortController();
    try {
      const page = await fetchComments(post.id, { cursor: commentsCursor, signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      let pageItems = page.flat;
      if (profile && pageItems.length > 0) {
        const liked = await fetchViewerLikedCommentIds(
          pageItems.map((c) => c.id),
          profile.id,
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        pageItems = pageItems.map((c) => ({ ...c, viewer_liked: liked.has(c.id) }));
      }
      setFlatComments((prev) => [...prev, ...pageItems]);
      setCommentsCursor(page.nextCursor);
      setHasMoreComments(page.nextCursor !== null);
    } catch (err: any) {
      if (ctrl.signal.aborted || err?.name === 'AbortError') return;
      console.error('[post] loadMoreComments failed', err);
    } finally {
      setLoadingMoreComments(false);
    }
  }, [commentsCursor, hasMoreComments, loadingMoreComments, post, profile]);

  // Build the nested tree from the merged flat list. Re-runs on every
  // pagination append + every optimistic like/reply mutation.
  const comments = useMemo(() => nestComments(flatComments), [flatComments]);

  // Deep-link: if URL has #comment=<id>, scroll it into view + highlight for 6s.
  const [permalinkId, setPermalinkId] = useState<string | null>(null);
  useEffect(() => {
    const hash = location.hash || '';
    const match = /#comment=([A-Za-z0-9\-]+)/.exec(hash);
    if (!match) return;
    if (loading) return;
    const id = match[1];
    const el = document.getElementById(`comment-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPermalinkId(id);
    const t = setTimeout(() => setPermalinkId(null), 6000);
    return () => clearTimeout(t);
  }, [loading, location.hash]);

  // Live-auction polling. Mirrors the plan: every 5s normally, every 2s in
  // the final minute. The effect is scoped to live auctions only - when
  // auctionState is null or not 'live', the early `return` leaves no timer
  // running, so non-auction posts pay zero polling cost.
  //
  // ends_at is in the dep array so that anti-snipe extensions (which push
  // ends_at forward inside place_bid) reset the interval - otherwise we'd
  // stay on the 2s fast-poll forever once we entered it.
  useEffect(() => {
    if (!post?.id || !auctionState || auctionState.state !== 'live') return;
    const endsAtMs = new Date(auctionState.ends_at).getTime();
    const msLeft = endsAtMs - Date.now();
    const interval = msLeft < 60_000 ? 2000 : 5000;
    const viewerId = profile?.id ?? null;
    const postId = post.id;
    const tick = async () => {
      try {
        const [next, bids] = await Promise.all([
          fetchAuctionForPost(postId, viewerId),
          fetchRecentBids(postId, RECENT_BIDS_LIMIT),
        ]);
        if (next) {
          setAuctionState((cur) => {
            // Carry forward viewer_watching from the current local state -             // fetchAuctionForPost doesn't re-resolve it on this batch path.
            next.viewer_watching = cur?.viewer_watching ?? next.viewer_watching;
            return next;
          });
        }
        setRecentBids(bids);
      } catch (err) {
        console.error('[auction-poll] failed', err);
      }
    };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [post?.id, auctionState?.state, auctionState?.ends_at, profile?.id]);

  async function handlePollToggle(optionId: string) {
    if (!profile || !post) return;
    const target = pollOptions.find((o) => o.id === optionId);
    if (!target) return;
    const wasVoted = target.viewer_voted;
    // Optimistic: flip viewer_voted and adjust vote_count.
    setPollOptions((prev) =>
      prev.map((o) =>
        o.id === optionId
          ? { ...o, viewer_voted: !wasVoted, vote_count: o.vote_count + (wasVoted ? -1 : 1) }
          : o,
      ),
    );
    try {
      await togglePollVote(post.id, optionId, profile.id, wasVoted);
    } catch (err) {
      console.error('[poll-vote] failed', err);
      setPollOptions((prev) =>
        prev.map((o) =>
          o.id === optionId
            ? { ...o, viewer_voted: wasVoted, vote_count: o.vote_count + (wasVoted ? 1 : -1) }
            : o,
        ),
      );
    }
  }

  async function handleRsvpToggle() {
    if (!profile || !post || !eventState) return;
    const wasGoing = !!eventState.viewer_going;
    const prev = eventState;
    setEventState({
      ...eventState,
      viewer_going: !wasGoing,
      going_count: eventState.going_count + (wasGoing ? -1 : 1),
    });
    try {
      await toggleEventRsvp(post.id, profile.id, wasGoing);
    } catch (err) {
      console.error('[event-rsvp] failed', err);
      setEventState(prev);
    }
  }

  function handleAddToCalendar() {
    if (!post || !eventState) return;
    const ics = buildIcs({
      title: post.title,
      startUtc: eventState.starts_at,
      endUtc: eventState.ends_at,
      description: post.dek ?? post.body_md ?? undefined,
      url: eventState.location_kind === 'online' ? (eventState.location_url ?? undefined) : undefined,
      location: eventState.location_kind === 'in_person'
        ? (eventState.location_address ?? undefined)
        : (eventState.location_url ?? undefined),
      uid: post.slug,
    });
    downloadIcs(`${post.slug}.ics`, ics);
  }

  async function handlePlaceBid(bidCents: number) {
    if (!profile || !post || !auctionState) return;
    setBidError(null);
    const prev = auctionState;
    // Direct bidding: the bid is exactly what they typed (in cents), so we can
    // optimistically show it as the new top + flag them as high bidder.
    // AuctionBlock keys hasBids (Current-bid label, Buy Now removal, sticky-bar
    // amount) on bid_count, so bump the counts too - otherwise the FIRST bid
    // renders a stale "Starting bid" block with Buy Now still offered. The
    // refetch right after reconciles against the canonical state (e.g. a
    // concurrent higher bid).
    const viewerAlreadyBid =
      auctionState.viewer_is_high_bidder ||
      recentBids.some((b) => b.bidder_id === profile.id);
    setAuctionState({
      ...auctionState,
      current_high_amount: bidCents,
      current_high_bidder_id: profile.id,
      viewer_is_high_bidder: true,
      bid_count: auctionState.bid_count + 1,
      bidder_count: auctionState.bidder_count + (viewerAlreadyBid ? 0 : 1),
    });
    try {
      await placeBid(post.id, bidCents);
      const [next, bids] = await Promise.all([
        fetchAuctionForPost(post.id, profile.id),
        fetchRecentBids(post.id, RECENT_BIDS_LIMIT),
      ]);
      if (next) {
        // Preserve watch state from the local snapshot (fetchAuctionForPost
        // here doesn't re-attach viewer_watching because we don't pass the
        // batch through fetchWatchedPostIds).
        next.viewer_watching = prev.viewer_watching;
        setAuctionState(next);
      }
      setRecentBids(bids);
    } catch (err: any) {
      console.error('[place-bid] failed', err);
      setAuctionState(prev);
      setBidError(mapBidError(err));
      // Card was removed/expired since we last checked - re-show the gate.
      if (String(err?.message ?? '').includes('no_card_on_file')) setCardOnFile(false);
    }
  }

  // Buy Now opens a checkout modal (address + live shipping + on-session
  // charge). After a successful purchase we refetch so the block flips to its
  // ended "lot is yours" state, where bought_now_at + viewer_is_high_bidder
  // drive the line so it survives reload.
  const handleBuyNowPurchased = useCallback(async () => {
    if (!profile || !post || !auctionState) return;
    const prev = auctionState;
    try {
      const [next, bids] = await Promise.all([
        fetchAuctionForPost(post.id, profile.id),
        fetchRecentBids(post.id, RECENT_BIDS_LIMIT),
      ]);
      if (next) {
        next.viewer_watching = prev.viewer_watching;
        setAuctionState(next);
      }
      setRecentBids(bids);
    } catch (err) {
      console.error('[buy-now] refresh failed', err);
    }
  }, [profile?.id, post?.id, auctionState]);

  async function handlePostLike() {
    if (!profile || !post) return;
    const wasLiked = liked;
    // Optimistic
    setLiked(!wasLiked);
    setLikeCount((n) => n + (wasLiked ? -1 : 1));
    try {
      const nextLiked = await togglePostLike(post.id, profile.id, wasLiked);
      setLiked(nextLiked);
    } catch (err) {
      console.error('[post-like] failed', err);
      setLiked(wasLiked);
      setLikeCount((n) => n + (wasLiked ? 1 : -1));
    }
  }

  async function handleAddComment(body: string, parentId: string | null) {
    if (!profile || !post) return;
    const created = await insertComment({
      postId: post.id,
      authorId: profile.id,
      body,
      parentId,
    });
    // Append to flat list. nestComments() will re-thread it under the
    // right parent on the next render. parent_id is preserved by
    // insertComment so threading is automatic.
    setFlatComments((prev) => [...prev, { ...created, parent_id: parentId ?? null }]);
    setCommentCount((n) => n + 1);
  }

  async function handleDeleteComment(commentId: string) {
    if (!profile) return;
    // parent_id cascades server-side, so a deleted top-level comment takes its
    // replies with it - drop the same subtree (the comment + any reply whose
    // parent is it) from the flat list, and decrement the count to match.
    const removed = new Set<string>([commentId]);
    flatComments.forEach((c) => { if (c.parent_id === commentId) removed.add(c.id); });
    const prev = flatComments;
    const prevCount = commentCount;
    setFlatComments((cs) => cs.filter((c) => !removed.has(c.id)));
    setCommentCount((n) => Math.max(0, n - removed.size));
    try {
      await deleteComment(commentId);
    } catch (err) {
      console.error('[comment-delete] failed', err);
      setFlatComments(prev);
      setCommentCount(prevCount);
    }
  }

  async function handleCommentLike(commentId: string, currentlyLiked: boolean) {
    if (!profile) return;
    // Mutate flat list. The render-time nestComments() picks the new
    // viewer_liked + like_count up automatically.
    const mutate = (mutator: (c: Comment) => Comment) =>
      setFlatComments((prev) => prev.map((c) => (c.id === commentId ? mutator(c) : c)));

    mutate((c) => ({
      ...c,
      viewer_liked: !currentlyLiked,
      like_count: c.like_count + (currentlyLiked ? -1 : 1),
    }));
    try {
      const next = await toggleCommentLike(commentId, profile.id, currentlyLiked);
      mutate((c) => ({ ...c, viewer_liked: next }));
    } catch (err) {
      console.error('[comment-like] failed', err);
      mutate((c) => ({
        ...c,
        viewer_liked: currentlyLiked,
        like_count: c.like_count + (currentlyLiked ? 1 : -1),
      }));
    }
  }

  if (loading) {
    return (
      <article className="post">
        <div className="container container--md" aria-busy="true" aria-label="Loading post">
          <div className="post__skel-back"><Skeleton variant="line" width={64} height={10} /></div>
          <div className="post__skel-eyebrow"><Skeleton variant="line" width={120} height={10} /></div>
          <div className="post__skel-title">
            <Skeleton variant="text" width="90%" height={28} />
            <Skeleton variant="text" width="72%" height={28} />
            <Skeleton variant="text" width="48%" height={28} />
          </div>
          <div className="post__skel-cover"><Skeleton variant="card" aspectRatio={16 / 9} /></div>
          <div className="post__skel-body">
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="94%" />
            <Skeleton variant="text" width="98%" />
            <Skeleton variant="text" width="88%" />
            <Skeleton variant="text" width="92%" />
            <Skeleton variant="text" width="60%" />
          </div>
          <div className="post__skel-bar">
            <Skeleton variant="line" width={100} height={32} />
            <Skeleton variant="line" width={80}  height={32} />
          </div>
          <div className="post__skel-body">
            <Skeleton variant="text" width="30%" height={20} />
          </div>
        </div>
      </article>
    );
  }
  if (notFound || !post) {
    return (
      <div className="container post">
        <EmptyState
          eyebrow="Not found"
          headline="This post isn't here."
          body="It may have been removed, or the link might be off."
          cta={<Button as={Link} to="/" variant="soft" size="sm">← Back to the feed</Button>}
        />
      </div>
    );
  }

  const drawer = post.drawers?.[0];
  // Video poster: the per-video thumbnail (media.thumbnail_url, set in the
  // wizard's media list) wins; the post-level feed thumbnail is the fallback.
  const cover = post.media?.thumbnail_url ?? post.thumbnail_url ?? undefined;
  const aspect = post.media?.aspect_ratio ?? 16 / 9;
  // For videos: pass mediaId to VideoPlayer - it fetches a signed/managed
  // playback URL. Legacy demo posts store a raw HLS URL in provider_id;
  // VideoPlayer's `src` prop is kept as a backward-compat fallback.
  const videoFallbackSrc =
    post.media?.kind === 'video' && post.media.provider_id?.startsWith('http')
      ? post.media.provider_id
      : null;
  const videoMediaId = post.media?.kind === 'video' ? post.media.id : null;

  const isTextPost = post.post_type === 'text';
  const isImagePost = post.post_type === 'image';
  const isVideoPost = post.post_type === 'video';

  // Ordered, mixed image+video items (from post_media; the query layer falls
  // back to the primary media for legacy un-backfilled posts). A lone video
  // keeps the dedicated player (preserves the legacy HLS `src` fallback);
  // anything else with media renders the swipe carousel + lightbox.
  const mediaItems: Media[] = post.media_items ?? [];
  const loneVideo = mediaItems.length === 1 && mediaItems[0].kind === 'video';
  const useCarousel = mediaItems.length > 0 && !loneVideo;
  const isRedesign = post.format === 'redesign';
  const traceUrl = post.resource_url ?? drawer?.default_resource_url ?? null;
  const isAuction = !!auctionState;
  // Stacked is the /saleroom2 A/B variant (old arrangement): its back link and
  // the grid it returns to live under /saleroom2; rail/default stays /saleroom.
  const isStacked = layout === 'stacked';
  const saleroomBase = isStacked ? '/saleroom2' : '/saleroom';

  // The lot gallery (left column on auction posts) - same hero/video/carousel
  // logic as the standard post body. Uses the centred pager DOTS (like posts),
  // not a thumbnail filmstrip (team, 2026-06-22).
  const galleryNode = (
    <>
      {mediaItems.length === 0 && !videoMediaId && !videoFallbackSrc && (
        <div className="post__hero" aria-hidden="true">
          <span className="post__hero-art"><Halftone type="bloom" /></span>
          {drawer && <span className="post__hero-tag">{drawer.name}</span>}
        </div>
      )}
      {!useCarousel && (videoMediaId || videoFallbackSrc) && (
        <div className="post__video">
          <VideoPlayer mediaId={videoMediaId} src={videoFallbackSrc} poster={cover} aspect={aspect} />
        </div>
      )}
      {useCarousel && <PostMedia items={mediaItems} aspect={aspect} />}
    </>
  );

  return (
    <article className={`post ${isTextPost ? 'post--text' : ''} ${isImagePost ? 'post--image' : ''} ${isAuction ? 'post--auction' : ''}`}>
      <div className={`container ${isAuction ? '' : 'container--md'}`}>
        {/* Back link only, left-aligned - no closet-name eyebrow (mirrors the
            Saleroom lot page; team request for a calmer, primitive-coherent
            header). */}
        <div className="post__topbar">
          <Link to={isAuction ? saleroomBase : '/'} className="post__back">
            {isAuction ? '← Back to the Saleroom' : '← Back'}
          </Link>
        </div>

        {auctionState ? (
          // Both layouts render the SAME AuctionBlock with identical props -
          // only the surrounding arrangement differs (rail = sticky buy box on
          // the right; stacked = single reading column, buy box below the copy).
          (() => {
            const layoutProps = {
              post,
              auction: auctionState,
              gallery: galleryNode,
              actions: (
                // A lot has ONE heart = favorite. It drives post_watchers, so
                // liking a lot lands it in Favorites + keeps the notify
                // pipeline. Sits in the like/share row like a normal post like;
                // the separate eye/watch placements were removed (team).
                <div className="lot__actions">
                  <ShareButton slug={post.slug} />
                  <WatchButton
                    postId={auctionState.post_id}
                    initialWatching={!!auctionState.viewer_watching}
                    initialCount={auctionState.watch_count}
                    variant="compact"
                    canWatch={auctionState.state !== 'ended'}
                  />
                </div>
              ),
              bidModule: (
                <AuctionBlock
                  auction={auctionState}
                  recentBids={recentBids}
                  viewerId={profile?.id ?? null}
                  canBid={!!profile}
                  isAuthor={!!profile && profile.id === post.author_id}
                  cardOnFile={cardOnFile}
                  postageQuote={postageQuote}
                  addresses={addresses}
                  selectedAddressId={selectedAddressId}
                  onSelectAddress={handleSelectAddress}
                  bidError={bidError}
                  onPlaceBid={handlePlaceBid}
                  onBuyNow={() => setShowCheckout(true)}
                  onAddCard={() => setShowAddCard(true)}
                  onDismissBidError={() => setBidError(null)}
                />
              ),
            };
            return isStacked
              ? <AuctionLayoutStacked {...layoutProps} />
              : <AuctionLayout {...layoutProps} />;
          })()
        ) : (
          <>
            {/* The media block plus its floating like/share actions. The actions
                sit right below the bottom-right of the media; on a multi-image
                post (--dots) they drop onto the carousel dots' line, otherwise
                they tuck just under the single image / video. */}
            <div
              className={`post__media-wrap${mediaItems.length > 1 ? ' post__media-wrap--dots' : ''}`}
              style={mediaW ? ({ ['--post-media-w' as string]: `${mediaW}px` } as React.CSSProperties) : undefined}
            >
              {mediaItems.length === 0 && !videoMediaId && !videoFallbackSrc && (
                <div className="post__hero" aria-hidden="true">
                  <span className="post__hero-art"><Halftone type="bloom" /></span>
                  {drawer && <span className="post__hero-tag">{drawer.name}</span>}
                </div>
              )}

              {!useCarousel && (videoMediaId || videoFallbackSrc) && (
                <div className="post__video">
                  <VideoPlayer
                    mediaId={videoMediaId}
                    src={videoFallbackSrc}
                    poster={cover}
                    aspect={aspect}
                  />
                </div>
              )}

              {useCarousel && <PostMedia items={mediaItems} aspect={aspect} onViewportWidth={setMediaW} />}

              <div className="post__bar post__media-actions">
                <ShareButton slug={post.slug} />
                <LikeButton n={likeCount} liked={liked} onToggle={handlePostLike} />
              </div>
            </div>

            {/* One white card holds the rest of the post: the description, the
                post's action buttons, etc. (mirrors the Saleroom lot's
                description card) so the content reads as a single contained
                block. The like/share now floats under the media (above). */}
            <div className="post__panel">
              {/* Title caps the card. The like/share that used to sit at its
                  top-right now floats under the media. */}
              <div className="post__head">
                {!post.hide_title && <Heading level="h2" className="post__title">{post.title}</Heading>}
              </div>

              {post.dek && <p className="post__subheading">{post.dek}</p>}

              {post.body_md && (isTextPost
                ? <RichTextView html={post.body_md} className="post__body" />
                : <PostCaption html={post.body_md} />)}

              {isRedesign && traceUrl && (
                <div className="post__resource">
                  <Button
                    as="a"
                    variant="primary"
                    size="sm"
                    href={traceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    ↓ Download trace resource
                  </Button>
                  <p className="post__resource-hint">Print it, sketch your redesign on top, then submit below.</p>
                </div>
              )}

              {post.buttons && post.buttons.length > 0 && <PostButtons buttons={post.buttons} />}
            </div>

            {isRedesign && <RedesignSubmissions postId={post.id} />}
          </>
        )}

        {showAddCard && (
          <AddCardModal
            postId={post.id}
            onClose={() => setShowAddCard(false)}
            onLinked={refreshCard}
          />
        )}

        {showCheckout && auctionState?.buy_now_price != null && (
          <BuyNowCheckoutModal
            postId={post.id}
            lotName={auctionState.lot_name ?? post.title ?? 'this lot'}
            buyNowPriceCents={auctionState.buy_now_price}
            initialAddressId={selectedAddressId}
            onClose={() => setShowCheckout(false)}
            onPurchased={handleBuyNowPurchased}
          />
        )}

        {eventState && (
          <EventBlock
            event={eventState}
            canRsvp={!!profile}
            onRsvpToggle={handleRsvpToggle}
            onAddToCalendar={handleAddToCalendar}
          />
        )}

        {pollOptions.length > 0 && (
          <PollBlock
            options={pollOptions}
            canVote={!!profile}
            onToggle={handlePollToggle}
          />
        )}

        {/* Non-auction posts carry like/share at the foot of the content card
            (see .post__panel above); auction lots keep their like/share in the
            quiet cluster under the gallery. */}

        <section className="post__comments" aria-label="Comments">
          {/* The denormalised posts.comment_count drifts (test rows inserted
              outside the trigger), so once the whole thread is loaded show the
              real loaded total; only fall back to the stored count while pages
              remain unfetched. */}
          <h2>Comments · {hasMoreComments ? commentCount : flatComments.length}</h2>
          {comments.length > 0 && (
            <>
              <ol className="comments">
                {comments.map((c) => (
                  <CommentItem
                    key={c.id}
                    comment={c}
                    postSlug={post.slug}
                    viewerId={profile?.id ?? null}
                    viewerIsStaff={profile?.role === 'admin' || profile?.role === 'developer'}
                    highlightId={permalinkId}
                    onLike={handleCommentLike}
                    onReply={(body) => handleAddComment(body, c.id)}
                    onDelete={handleDeleteComment}
                  />
                ))}
              </ol>
              {hasMoreComments && (
                <div className="post__comments-more">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={loadMoreComments}
                    disabled={loadingMoreComments}
                  >
                    {loadingMoreComments ? 'Loading…' : 'Load more comments'}
                  </Button>
                </div>
              )}
            </>
          )}
          {profile
            ? <CommentComposer onSubmit={(body) => handleAddComment(body, null)} />
            : <p className="comment-composer__signin">Sign in to join the conversation.</p>}
        </section>
      </div>
    </article>
  );
}

function CommentItem({
  comment,
  postSlug,
  viewerId,
  viewerIsStaff,
  highlightId,
  onLike,
  onReply,
  onDelete,
  isReply = false,
}: {
  comment: Comment;
  postSlug: string;
  viewerId: string | null;
  /** Admin/developer: may delete ANY comment, not just their own. */
  viewerIsStaff: boolean;
  highlightId: string | null;
  onLike: (id: string, currentlyLiked: boolean) => Promise<void>;
  onReply: (body: string) => Promise<void>;
  onDelete: (id: string) => void;
  /** Replies render one notch smaller (avatar + spacing) - Instagram-style. */
  isReply?: boolean;
}) {
  const [showReply, setShowReply] = useState(false);
  // Replies collapse by default (Instagram-style); a toggle reveals them.
  const [showReplies, setShowReplies] = useState(false);
  // Two-tap delete: the bin arms an inline confirm rather than a native dialog.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const authorName = authorHandle(comment.author);
  const founding = comment.author?.is_founding_member ?? false;
  const authorTo = profilePath(comment.author);
  const highlighted = highlightId === comment.id;
  const replyCount = comment.replies?.length ?? 0;
  const isOwn = viewerId != null && comment.author_id === viewerId;
  // Owners delete their own; admins/developers may delete anyone's (moderation).
  const canDelete = isOwn || viewerIsStaff;
  const ref = useRef<HTMLLIElement>(null);

  // Auto-expand if a permalinked comment lives inside this thread, so the
  // deep-link scroll/highlight can find it.
  useEffect(() => {
    if (highlightId && comment.replies?.some((r) => r.id === highlightId)) {
      setShowReplies(true);
    }
  }, [highlightId, comment.replies]);

  const avatarSize = isReply ? '2xs' : 'xs';

  return (
    <li
      ref={ref}
      id={`comment-${comment.id}`}
      className={`comment ${isReply ? 'comment--reply' : ''} ${highlighted ? 'comment--highlighted' : ''}`}
    >
      {authorTo ? (
        <Link to={authorTo} className="comment__avatar-link" aria-label={`View @${authorName}`}>
          <Avatar name={authorName} src={comment.author?.avatar_url} size={avatarSize} />
        </Link>
      ) : (
        <Avatar name={authorName} src={comment.author?.avatar_url} size={avatarSize} />
      )}
      <div className="comment__body">
        {/* Handle + founding seal on a quiet first line; the comment itself is
            the loud element below it. */}
        <p className="comment__head">
          {authorTo ? (
            <Link to={authorTo} className="comment__author comment__author--link">@{authorName}</Link>
          ) : (
            <span className="comment__author">@{authorName}</span>
          )}
          {founding && <FoundingBadge size={isReply ? 13 : 14} className="comment__founding" />}
          {/* The age sits right after the handle (or the founding seal), quiet
              and grey - it reads as a byline, not a row of its own. */}
          <span className="comment__time">{formatRelative(comment.created_at)}</span>
        </p>
        <p className="comment__text">{comment.body}</p>
        <div className="comment__meta">
          {/* Low-emphasis actions (reply / bin); the like is pushed to the far
              right via margin (Instagram). */}
          {comment.parent_id === null && (
            <button
              type="button"
              className="comment__action"
              onClick={() => setShowReply((v) => !v)}
            >
              Reply
            </button>
          )}
          {canDelete && (
            confirmDelete ? (
              <span className="comment__delete-confirm">
                <button
                  type="button"
                  className="comment__action comment__action--danger"
                  onClick={() => onDelete(comment.id)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="comment__action"
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="comment__action comment__action--icon"
                onClick={() => setConfirmDelete(true)}
                aria-label={isOwn ? 'Delete your comment' : 'Delete comment'}
                title="Delete"
              >
                <TrashIcon />
              </button>
            )
          )}
          <button
            type="button"
            className={`comment__like ${comment.viewer_liked ? 'comment__like--on' : ''}`}
            onClick={() => onLike(comment.id, !!comment.viewer_liked)}
            aria-pressed={!!comment.viewer_liked}
            aria-label={comment.viewer_liked ? 'Unlike comment' : 'Like comment'}
          >
            <HeartIcon on={!!comment.viewer_liked} size={isReply ? 16 : 18} />
            {comment.like_count > 0 && <span className="comment__like-count">{comment.like_count}</span>}
          </button>
        </div>

        {showReply && (
          <CommentComposer
            compact
            onSubmit={async (body) => {
              await onReply(body);
              setShowReply(false);
            }}
          />
        )}

        {replyCount > 0 && (
          <button
            type="button"
            className="comment__replies-toggle"
            onClick={() => setShowReplies((v) => !v)}
            aria-expanded={showReplies}
          >
            {showReplies ? 'Hide replies' : `View ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
          </button>
        )}

        {showReplies && replyCount > 0 && (
          <ol className="comments comments--nested">
            {comment.replies!.map((r) => (
              <CommentItem
                key={r.id}
                comment={r}
                postSlug={postSlug}
                viewerId={viewerId}
                viewerIsStaff={viewerIsStaff}
                highlightId={highlightId}
                onLike={onLike}
                onReply={async () => {}}
                onDelete={onDelete}
                isReply
              />
            ))}
          </ol>
        )}
      </div>
    </li>
  );
}

function CommentComposer({
  compact,
  onSubmit,
}: {
  compact?: boolean;
  onSubmit: (body: string) => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow: the field starts one line tall (matching the Post button beside
  // it) and grows with the text up to TWO lines, then scrolls - so it never
  // balloons into a dominating box.
  const autosize = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || 21;
    const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const border = el.offsetHeight - el.clientHeight; // top+bottom border
    const max = lh * 2 + pad + border;                // two-line cap
    el.style.height = `${Math.min(el.scrollHeight + border, max)}px`;
    el.style.overflowY = el.scrollHeight + border > max ? 'auto' : 'hidden';
  }, []);

  // Re-fit on every value change (typing AND the reset-to-empty after a submit),
  // and once more after web fonts settle - the brand font swaps in after first
  // paint and changes the line metrics, so an early measurement can read tall.
  useEffect(() => {
    autosize();
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(autosize).catch(() => {});
    }
  }, [value, autosize]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(value.trim());
      setValue('');
    } catch (err) {
      // Don't clear the textarea - the user shouldn't lose what they wrote.
      console.error('[comment] submit failed', err);
      setError("That comment didn't post. Try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className={`comment-composer ${compact ? 'comment-composer--compact' : ''}`} onSubmit={submit}>
      <textarea
        ref={taRef}
        rows={1}
        placeholder={compact ? 'Reply…' : 'Leave a comment…'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={4000}
      />
      <Button type="submit" variant="primary" size="sm" disabled={!value.trim() || busy}>
        {busy ? 'Posting…' : 'Post'}
      </Button>
      {error && <p className="comment-composer__error" role="alert">{error}</p>}
    </form>
  );
}

function PollBlock({
  options,
  canVote,
  onToggle,
}: {
  options: PollOption[];
  canVote: boolean;
  onToggle: (optionId: string) => void;
}) {
  const totalVotes = options.reduce((n, o) => n + o.vote_count, 0);
  // Leader-relative bar fill: the winning option fills fully, others
  // scale against it. Falls back to 1 to avoid divide-by-zero before any
  // votes are cast.
  const leader = Math.max(1, ...options.map((o) => o.vote_count));
  return (
    <div className="poll" role="group" aria-label="Poll">
      <ul className="poll__list">
        {options.map((o) => {
          const pct = totalVotes === 0 ? 0 : Math.round((o.vote_count / totalVotes) * 100);
          const fill = Math.round((o.vote_count / leader) * 100);
          return (
            <li key={o.id} className={`poll__item ${o.viewer_voted ? 'poll__item--on' : ''}`}>
              <button
                type="button"
                className="poll__btn"
                onClick={() => onToggle(o.id)}
                disabled={!canVote}
                aria-pressed={o.viewer_voted}
              >
                <span className="poll__bar" aria-hidden="true" style={{ width: `${fill}%` }} />
                <span className="poll__check" aria-hidden="true">{o.viewer_voted ? '✓' : ''}</span>
                <span className="poll__label">{o.label}</span>
                <span className="poll__count">{o.vote_count} · {pct}%</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="poll__meta">
        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'} · pick any that apply
        {!canVote && ' · sign in to vote'}
      </p>
    </div>
  );
}

function EventBlock({
  event,
  canRsvp,
  onRsvpToggle,
  onAddToCalendar,
}: {
  event: PostEvent;
  canRsvp: boolean;
  onRsvpToggle: () => void;
  onAddToCalendar: () => void;
}) {
  const now = Date.now();
  const startMs = new Date(event.starts_at).getTime();
  const endMs = event.ends_at ? new Date(event.ends_at).getTime() : null;
  // An event is "ended" when either: it has an end and that end has passed,
  // OR there's no end and the start was more than an hour ago. The 1h fallback
  // matches the ICS default-end behaviour.
  const eventEnded =
    endMs !== null ? endMs < now : startMs + 60 * 60 * 1000 < now;

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(event.starts_at));
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(event.starts_at));

  const isOnline = event.location_kind === 'online';
  const going = !!event.viewer_going;

  return (
    <section className="event" aria-label="Event">
      <p className="event__eyebrow">Event</p>
      <p className="event__when">
        {dateLabel} · {timeLabel}
      </p>
      <p className="event__where">
        {isOnline ? (
          <>
            <span aria-hidden="true">🎥</span> Online
            {event.location_url && (
              <>
                {' · '}
                <a
                  href={event.location_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event__join"
                >
                  Join link
                </a>
              </>
            )}
          </>
        ) : (
          <>
            <span aria-hidden="true">📍</span> {event.location_address ?? 'In person'}
          </>
        )}
      </p>

      {eventEnded ? (
        <p className="event__ended">This event has ended.</p>
      ) : (
        <div className="event__actions">
          <button
            type="button"
            className={`pill-action ${going ? 'pill-action--on' : ''}`}
            onClick={onRsvpToggle}
            disabled={!canRsvp}
            aria-pressed={going}
          >
            <span aria-hidden="true">{going ? '♥' : '♡'}</span>
            {going ? "You're going" : "I'm going"}
          </button>
          <span className="event__count">
            {event.going_count} {event.going_count === 1 ? 'going' : 'going'}
          </span>
          <button
            type="button"
            className="pill-action"
            onClick={onAddToCalendar}
          >
            Add to my calendar
          </button>
        </div>
      )}
    </section>
  );
}

function AuctionBlock({
  auction,
  recentBids,
  viewerId,
  canBid,
  isAuthor,
  cardOnFile,
  postageQuote,
  addresses,
  selectedAddressId,
  onSelectAddress,
  bidError,
  onPlaceBid,
  onBuyNow,
  onAddCard,
  onDismissBidError,
}: {
  auction: PostAuction;
  recentBids: AuctionBid[];
  viewerId: string | null;
  canBid: boolean;
  isAuthor: boolean;
  /** Viewer's bidding-card state: null = still loading, true/false once known. */
  cardOnFile: boolean | null;
  /** Live postage estimate for the viewer's chosen address. */
  postageQuote: PostageQuote | null;
  /** The viewer's address book (for the per-lot delivery selector). */
  addresses: ShippingAddress[];
  selectedAddressId: string | null;
  onSelectAddress: (addressId: string) => void;
  bidError: string | null;
  onPlaceBid: (bidCents: number) => void;
  onBuyNow: () => void;
  onAddCard: () => void;
  onDismissBidError: () => void;
}) {
  // Direct bidding, all maths in CENTS: the first bid must meet the lot's
  // starting bid; every later bid must beat the current top by at least $1.00.
  // Bids are whole dollars only, so the floor rounds UP to the next whole
  // dollar (a fractional min_bid like $40.50 becomes a $41 minimum).
  const rawMinNextCents = auction.current_high_amount > 0
    ? auction.current_high_amount + 100
    : Math.max(100, auction.min_bid);
  const minNextCents = Math.ceil(rawMinNextCents / 100) * 100;
  // One price field, whole dollars only. The field always shows "<int>.00" as a
  // SINGLE input value (so the digits and the ".00" share one font + baseline,
  // no gaps); members type integers only and the caret is pinned before the dot.
  const [bidStr, setBidStr] = useState<string>('');
  const setBid = (raw: string) => {
    // Take the integer part only (everything before the dot), digits, cap 7.
    setBidStr(raw.split('.')[0].replace(/\D/g, '').slice(0, 7));
  };
  // What the input shows: "" when empty (placeholder takes over), else "<int>.00".
  const bidDisplay = bidStr === '' ? '' : `${bidStr}.00`;
  // Keep the caret just before the ".00" so typing always edits the dollars.
  const clampCaret = () => {
    const el = bidInputRef.current;
    if (!el) return;
    const dot = el.value.indexOf('.');
    if (dot < 0) return;
    const s = el.selectionStart ?? 0;
    const e = el.selectionEnd ?? 0;
    if (s > dot || e > dot) el.setSelectionRange(Math.min(s, dot), Math.min(e, dot));
  };
  useLayoutEffect(() => {
    const el = bidInputRef.current;
    if (!el || document.activeElement !== el) return;
    const pos = bidStr.length; // immediately before the "."
    el.setSelectionRange(pos, pos);
  }, [bidStr]);
  // The typed bid, in cents (whole dollars * 100).
  const typedCents = (parseInt(bidStr, 10) || 0) * 100;
  const hasBidInput = bidStr !== '';
  const bidValid = hasBidInput && typedCents >= minNextCents;
  // Placeholder is the whole-dollar minimum with the same fixed ".00", e.g. "41.00".
  const bidPlaceholder = `${minNextCents / 100}.00`;
  // Reset the input whenever the live floor moves (someone else bid).
  useEffect(() => { setBidStr(''); }, [auction.current_high_amount, auction.ends_at]);

  const isLive = auction.state === 'live';
  const isEnded = auction.state === 'ended';
  const hasBids = auction.bid_count > 0;
  // Postage: the viewer's live estimate when one exists (067), else the lot's
  // flat figure (cents; null = unspecified, 0 = free). Estimates carry an
  // "est." marker - the exact postage is confirmed before the winner charge.
  const postageIsEstimate = postageQuote != null;
  const postage = postageQuote?.amount_cents ?? auction.shipping_cost;

  // Live receipt under the input: the bid figure floors at the minimum so
  // the line always shows a placeable total, even before they type.
  const receiptCents = typedCents >= minNextCents ? typedCents : minNextCents;

  function submitBid(e: React.FormEvent) {
    e.preventDefault();
    if (!bidValid) return;
    onPlaceBid(typedCents);
  }

  // Buy Now is offered throughout the live window to any member who could
  // bid: buying instantly tops the current bid and ends the auction. Once
  // live bidding reaches/passes the Buy Now price it can no longer win, so it
  // stays visible but disabled with a note (mirrors the buy_now RPC, 068).
  const buyNowOffered =
    auction.buy_now_price != null && isLive &&
    canBid && !isAuthor && cardOnFile === true;
  const buyNowExceeded =
    buyNowOffered && hasBids && (auction.current_high_amount ?? 0) >= auction.buy_now_price!;

  // Mobile sticky bid bar: only while live, and only once the bid card has
  // scrolled out of view (IntersectionObserver on the stack).
  const stackRef = useRef<HTMLDivElement>(null);
  const bidInputRef = useRef<HTMLInputElement>(null);
  const [stackInView, setStackInView] = useState(true);
  useEffect(() => {
    if (!isLive) return;
    const el = stackRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setStackInView(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [isLive]);
  const jumpToBidCard = useCallback(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    stackRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    bidInputRef.current?.focus({ preventScroll: true });
  }, []);

  // Viewer-state messaging. The big high-bidder/outbid status lives in the
  // top band's right-hand space (bidderStatus); the form band keeps a quiet
  // prompt line. "Outbid" detection looks for the viewer in recentBids when
  // they're not the current high bidder. recentBids is capped at the last 10
  // so a long bid war could push the viewer's row out of the window; that's
  // a known best-effort limitation.
  const viewerHasBid = viewerId != null && recentBids.some((b) => b.bidder_id === viewerId);
  const bidderStatus: { kind: 'high' | 'outbid'; text: string } | null =
    isLive && canBid && !isAuthor
      ? auction.viewer_is_high_bidder
        ? { kind: 'high', text: "You're the highest bidder!" }
        : viewerHasBid
          ? { kind: 'outbid', text: "You've been outbid." }
          : null
      : null;
  let viewerMessage: string | null = null;
  if (isLive) {
    if (isAuthor) {
      viewerMessage = 'This is your lot, you can\'t bid on it.';
    } else if (!canBid) {
      viewerMessage = 'Members can bid.';
    } else if (cardOnFile === true) {
      viewerMessage = 'Place your bid below';
    }
  }

  // The bid module that fills the lot rail: a price block (current bid ->
  // shipping -> time left -> bids) then the form band (status -> input ->
  // Place bid -> Buy It Now). Once the lot ends it collapses to the calm
  // results panel; a Buy Now winner also gets a confirmation line, driven
  // off bought_now_at + viewer_is_high_bidder so it survives reload.
  const wonByViewer = isEnded && !!auction.viewer_is_high_bidder && (hasBids || !!auction.bought_now_at);
  const endedDateLabel = new Date(auction.ends_at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const showFormBand =
    !!viewerMessage || !!bidError || (isLive && canBid && !isAuthor && cardOnFile !== null);

  return (
    <>
    <div className="bidstack" ref={stackRef}>
    <section className={`bidblock ${isEnded ? 'bidblock--ended' : ''}`} aria-label="Bidding">
      {isEnded && (
        <div className="bidblock__result">
          {/* A results panel, not a black tombstone: outcome + activity + an exit
              (price-free per team decision). Proven pattern: eBay/Catawiki ended lot. */}
          <Badge tone="neutral">Bidding closed</Badge>
          <p className="bidblock__result-headline">{hasBids ? 'Sold' : 'No bids'}</p>
          <p className="bidblock__result-meta">
            {hasBids && <>{auction.bid_count} {auction.bid_count === 1 ? 'bid' : 'bids'} · </>}
            Ended {endedDateLabel}
          </p>
          {wonByViewer && (
            <p className="bidblock__yours">
              You won this lot - we'll confirm your order and charge your card on file.
            </p>
          )}
          <Button as={Link} to="/saleroom" variant="secondary" size="sm" className="bidblock__result-back">
            Back to the Saleroom
          </Button>
        </div>
      )}
      {!isEnded && (
      <>
      {/* Price block: current bid (the one loud figure) -> shipping under it ->
          time left -> bids/watching. One quiet left-aligned column. */}
      <div className="bidblock__price">
        <span className="bidblock__label">{hasBids ? 'Current bid' : 'Starting bid'}</span>
        <span className="bidblock__amount">
          {dollars(hasBids ? auction.current_high_amount : auction.min_bid)}
        </span>
        {postage !== null && (
          <span
            className="bidblock__ship"
            title={postageIsEstimate
              ? 'Estimated for your delivery address - exact postage is confirmed before your card is charged.'
              : undefined}
          >
            {postage === 0
              ? 'Free shipping'
              : <>+ {dollars(postage)} {postageIsEstimate ? 'est. shipping' : 'shipping'}</>}
          </span>
        )}
        <AuctionCountdown auction={auction} />
        {/* Bids are anonymous - we surface the count, never who holds the top bid. */}
        <p className="bidblock__meta">
          <strong>{auction.bid_count}</strong> {auction.bid_count === 1 ? 'bid' : 'bids'}
          <span className="bidblock__meta-dot" aria-hidden="true">·</span>
          <strong>{auction.watch_count}</strong> {auction.watch_count === 1 ? 'favorite' : 'favorites'}
        </p>
      </div>

      {showFormBand && (
      <div className="bidblock__formband">
      {/* One status line above the input (team-tuned treatment, kept): only
          shows when the viewer is winning / outbid. */}
      {bidderStatus && (
        <p className={`bidblock__status bidblock__status--${bidderStatus.kind}`}>
          {bidderStatus.text}
        </p>
      )}

      {viewerMessage && (
        <p className="bidblock__viewer">{viewerMessage}</p>
      )}

      {isLive && canBid && !isAuthor && cardOnFile === false && (
        <div className="bidblock__cardgate">
          <Button type="button" variant="primary" size="md" fullWidth onClick={onAddCard}>
            Register to bid
          </Button>
          <p className="bidblock__help">
            A quick one-time setup, your details and a card on file. You're only charged if you win.
          </p>
        </div>
      )}

      {isLive && canBid && !isAuthor && cardOnFile === true && (
        <>
        {addresses.length > 0 && (
          <Field label="Deliver to" htmlFor="lot-address" className="mb-3">
            <Select
              id="lot-address"
              value={selectedAddressId ?? ''}
              onChange={(e) => onSelectAddress(e.target.value)}
            >
              {addresses.map((a) => (
                <option key={a.id} value={a.id}>
                  {[a.label, a.city, a.country].filter(Boolean).join(' · ')}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <form className="bidblock__form" onSubmit={submitBid}>
          <p className="bidblock__help">
            Enter {dollars(minNextCents)} or more, highest bid wins.
          </p>
          {/* One price field: $ then a whole-dollar amount with a FIXED ".00".
              The whole "<int>.00" is ONE input value, so the digits and the
              ".00" share one font, weight and baseline (no gaps, no offset).
              Members type integers only; the caret is pinned before the dot. */}
          <div
            className="bidblock__field"
            onMouseDown={(e) => {
              if (e.target !== bidInputRef.current) {
                e.preventDefault();
                bidInputRef.current?.focus();
              }
            }}
          >
            <span className="bidblock__currency" aria-hidden="true">$</span>
            <input
              ref={bidInputRef}
              type="text"
              inputMode="numeric"
              placeholder={bidPlaceholder}
              value={bidDisplay}
              onChange={(e) => setBid(e.target.value)}
              onClick={clampCaret}
              onKeyUp={clampCaret}
              onFocus={clampCaret}
              className="bidblock__input"
              aria-label="Your bid in whole US dollars"
            />
          </div>
          <Button
            type="submit"
            variant="accent"
            size="md"
            fullWidth
            disabled={!bidValid}
          >
            Place bid
          </Button>
          {postage !== null && postage > 0 && (
            <p className="bidblock__receipt">
              Bid {dollars(receiptCents)} + {dollars(postage)} {postageIsEstimate ? 'est. shipping' : 'shipping'} ={' '}
              <b>{dollars(receiptCents + postage)}</b>
            </p>
          )}
        </form>

        {buyNowOffered && (
          <div className="bidblock__buynow">
            <Button
              type="button"
              variant="secondary"
              size="md"
              fullWidth
              onClick={onBuyNow}
              disabled={buyNowExceeded}
            >
              Buy It Now · {dollars(auction.buy_now_price!)}
            </Button>
            <p className="bidblock__buynote">
              {buyNowExceeded
                ? 'Bidding has passed the Buy It Now price.'
                : 'Skip the wait - check out now and the lot is yours.'}
            </p>
          </div>
        )}
        </>
      )}

      {bidError && (
        <p className="bidblock__error" role="alert" onClick={onDismissBidError}>
          {bidError}
        </p>
      )}
      </div>
      )}
      </>
      )}
    </section>
    </div>
    {/* Member bidding shortcut only: viewers who can't bid (author,
        non-members, no card on file) get no sticky CTA - jumping them to a
        card with no bid form would be a dead end. */}
    {isLive && canBid && !isAuthor && cardOnFile === true && !stackInView && (
      <StickyBidBar auction={auction} onJump={jumpToBidCard} />
    )}
    </>
  );
}

/** Mobile-only sticky bid bar (CSS hides it above 880px): pinned to the
 *  bottom while a live lot's bid card is scrolled off-screen. Shows the
 *  current/starting amount + a compact ticking clock; the pill scrolls back
 *  to the bid card and focuses the input. */
function StickyBidBar({ auction, onJump }: { auction: PostAuction; onJump: () => void }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, new Date(auction.ends_at).getTime() - now);
  const totalSec = Math.floor(remaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(Math.floor((totalSec % 86400) / 3600))}:${pad(Math.floor((totalSec % 3600) / 60))}:${pad(totalSec % 60)}`;
  const urgent = remaining < 60 * 60 * 1000;
  const amount = auction.bid_count > 0 ? auction.current_high_amount : auction.min_bid;
  return (
    <div className="bidbar" role="region" aria-label="Live bidding shortcut">
      <div className="bidbar__info">
        <span className="bidbar__price">{dollars(amount)}</span>
        <span className={`bidbar__time ${urgent ? 'bidbar__time--urgent' : ''}`}>
          Ends {days > 0 ? `${days}d ` : ''}{clock}
        </span>
      </div>
      <Button variant="accent" size="sm" className="ml-auto" onClick={onJump}>Place bid</Button>
    </div>
  );
}

/** State pill text for the lot header ("LIVE" / "OPENS in 6h" / "ENDED"). */
function auctionStatePill(a: PostAuction): string {
  if (a.state === 'live') return 'LIVE';
  if (a.state === 'scheduled') return `OPENS ${relativeTime(a.starts_at)}`;
  return 'ENDED';
}

/** The lot page layout (eBay/Catawiki split): gallery left (sticky), a buy rail
 *  right (sticky) capped by the lot header + carrying the bid module, then a
 *  full-width band below for the description + the Material/Size/Shipping
 *  disclosures. On mobile it collapses to one column in reading order:
 *  gallery -> header -> bid module -> details -> description. */
function AuctionLayout({
  post,
  auction,
  gallery,
  bidModule,
  actions,
}: {
  post: PostType;
  auction: PostAuction;
  gallery: React.ReactNode;
  bidModule: React.ReactNode;
  /** Quiet like/share cluster, built by the parent (which holds the like
   *  state + slug) and dropped under the gallery. */
  actions: React.ReactNode;
}) {
  const heading = (!post.hide_title && post.title) || auction.lot_name || post.title;
  const hasDetails = !!(auction.material_info || auction.size_info || auction.shipping_info);
  const stateTone = auction.state === 'live' ? 'accent' : auction.state === 'ended' ? 'neutral' : 'neutral';
  return (
    <div className="lot">
      <div className="lot__gallery">
        {gallery}
        {/* Quiet like/share cluster, tucked under the gallery (eBay pattern):
            unbordered, low-emphasis, not a full-width bar. */}
        {actions}
      </div>

      {/* Right rail = one cohesive buy box: a white surface (the rail itself)
          that the lot header caps and the bid module fills, separated by a
          single internal hairline. Watch (an auction action) sits top-right of
          the box, in the header row. Sticky on desktop so price + Place bid
          stay above the fold. */}
      <div className="lot__rail">
        <header className="lot__header">
          <div className="lot__tags">
            <Badge tone={stateTone} solid={auction.state === 'live'}>{auctionStatePill(auction)}</Badge>
            <Badge tone="ink" solid>LOT {auction.lot_number}</Badge>
          </div>
          <h1 className="lot__name">{heading}</h1>
        </header>
        {bidModule}
      </div>

      {/* Full-width band below the two columns. The description leads (the
          lot's story, given prominence) then the Material/Size/Shipping
          disclosures sit quietly below it. */}
      {(hasDetails || post.body_md) && (
        <div className="lot__detail">
          {post.body_md && (
            <div className="lot__story">
              <p className="lot__story-label">Description</p>
              <RichTextView html={post.body_md} className="lot__desc" />
            </div>
          )}
          {hasDetails && (
            <div className="lot__disclosures">
              {auction.material_info && (
                <Disclosure title="Material">
                  <p className="lot__detail-text">{auction.material_info}</p>
                </Disclosure>
              )}
              {auction.size_info && (
                <Disclosure title="Size details">
                  <p className="lot__detail-text">{auction.size_info}</p>
                </Disclosure>
              )}
              {auction.shipping_info && (
                <Disclosure title="Shipping">
                  <p className="lot__detail-text">{auction.shipping_info}</p>
                </Disclosure>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The stacked lot layout (/saleroom2 A/B variant): the OLD arrangement, but
 *  built from the same refined pieces. One centred reading column at a ~66ch
 *  measure. Order top to bottom: lot header (anchored title lockup) -> gallery
 *  -> details + description -> the buy box (the same bid module) -> the quiet
 *  like/share + Watch cluster. It reuses every .lot__* / .bidblock* class the
 *  rail layout uses; only the .lot2__* wrappers are stacked-specific. The bid
 *  module is byte-identical to the rail's - the buy-box surface is supplied by
 *  the .lot2__buybox wrapper (which shares .lot__rail's surface rule). */
function AuctionLayoutStacked({
  post,
  auction,
  gallery,
  bidModule,
  actions,
}: {
  post: PostType;
  auction: PostAuction;
  gallery: React.ReactNode;
  bidModule: React.ReactNode;
  actions: React.ReactNode;
}) {
  const heading = (!post.hide_title && post.title) || auction.lot_name || post.title;
  const hasDetails = !!(auction.material_info || auction.size_info || auction.shipping_info);
  const stateTone = auction.state === 'live' ? 'accent' : auction.state === 'ended' ? 'neutral' : 'neutral';
  return (
    <div className="lot2">
      {/* The lot title lockup, above two SEPARATE cards: the media in its own
          container, the descriptive elements (description + dropdowns + the
          quiet like/share row) in theirs. The bid box is a third block below. */}
      <header className="lot__header lot2__header">
        <div className="lot__tags">
          <Badge tone={stateTone} solid={auction.state === 'live'}>{auctionStatePill(auction)}</Badge>
          <Badge tone="ink" solid>LOT {auction.lot_number}</Badge>
        </div>
        <h1 className="lot__name">{heading}</h1>
      </header>

      <div className="lot2__top">
        {/* Media card (~40%). */}
        <div className="lot2__media">{gallery}</div>

        {/* Descriptive card (~60%): the description reads first, then the
            Material/Size/Shipping dropdowns, then the quiet like/share row. */}
        <div className="lot2__detail-box">
          {post.body_md && (
            <div className="lot2__story lot__story">
              <p className="lot__story-label">Description</p>
              <RichTextView html={post.body_md} className="lot__desc" />
            </div>
          )}
          {hasDetails && (
            <div className="lot__disclosures lot2__disclosures">
              {auction.material_info && (
                <Disclosure title="Material">
                  <p className="lot__detail-text">{auction.material_info}</p>
                </Disclosure>
              )}
              {auction.size_info && (
                <Disclosure title="Size details">
                  <p className="lot__detail-text">{auction.size_info}</p>
                </Disclosure>
              )}
              {auction.shipping_info && (
                <Disclosure title="Shipping">
                  <p className="lot__detail-text">{auction.shipping_info}</p>
                </Disclosure>
              )}
            </div>
          )}
          <div className="lot2__actions">
            {actions}
          </div>
        </div>
      </div>

      {/* 2. The buy box: a full-width block below the panel, sitting right above
          the comments. Watch sits top-right of the box; same bid module, same
          white buy-box surface. */}
      <div className="lot2__buybox">
        {bidModule}
      </div>
    </div>
  );
}

/** Live ticking countdown - the always-red "Ends in" line under the price. Counts down to
 *  starts_at while scheduled, to ends_at while live. Never rendered once the
 *  lot ends (the card collapses to the SOLD/UNSOLD stamp), so the ended
 *  guard just returns nothing. */
function AuctionCountdown({ auction }: { auction: PostAuction }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (auction.state === 'ended') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [auction.state]);

  if (auction.state === 'ended') return null;

  const target = auction.state === 'scheduled' ? auction.starts_at : auction.ends_at;
  const remaining = Math.max(0, new Date(target).getTime() - now);
  const totalSec = Math.floor(remaining / 1000);
  // "4 days 10:30:45" - the days figure splits out in words, the rest
  // stays a HH:MM:SS clock.
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const hms = [hours, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
  const clock = days > 0 ? `${days} ${days === 1 ? 'day' : 'days'} ${hms}` : hms;
  const label = auction.state === 'scheduled' ? 'Opens in' : 'Ends in';
  // Under an hour on a live lot - flag it so the clock can pulse / go red.
  const urgent = auction.state === 'live' && remaining < 60 * 60 * 1000;

  return (
    <div className={`countdown ${urgent ? 'countdown--urgent' : ''}`}>
      <span className="countdown__label">{label}</span>
      <span className="countdown__clock" role="timer">{clock}</span>
    </div>
  );
}

/** Multi-media post body: the swipe carousel plus the full-screen lightbox it
 *  opens. Local state lives here so the parent's hook order stays stable. */
function PostMedia({ items, aspect, thumbs, onViewportWidth }: { items: Media[]; aspect: number; thumbs?: boolean; onViewportWidth?: (w: number) => void }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  return (
    <>
      <div className="post__media">
        <MediaCarousel
          items={items}
          aspect={aspect}
          variant={thumbs ? 'thumbs' : 'dots'}
          onOpenLightbox={(i) => setLightboxIndex(i)}
          onViewportWidth={onViewportWidth}
        />
      </div>
      {lightboxIndex !== null && (
        <Lightbox
          items={items}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </>
  );
}

/** The post description rendered as an Instagram-style caption: a "Description"
 *  eyebrow over the copy, clamped to a few lines with a soft fade and a
 *  more/less toggle. The toggle only appears when the copy actually overflows
 *  the clamp (measured against the rendered height, so it works for any length
 *  of rich text). */
function PostCaption({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only measure while collapsed - expanded removes the clamp, so clientHeight
    // would equal scrollHeight and falsely read as "fits". Once we know it can
    // expand we keep the toggle, so "See less" stays available.
    const check = () => {
      if (!expanded) setCanExpand(el.scrollHeight - el.clientHeight > 4);
    };
    check();
    // A web-font swap reflows the text (more/fewer lines) AFTER the first
    // measurement, so re-check once fonts settle - otherwise a description that
    // overflows only in the real font never gets its "See more" button.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(check).catch(() => {});
    }
    // Re-measure on any layout change (column width, font load, content swap).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    window.addEventListener('resize', check);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [html, expanded]);

  return (
    <div className="post__story">
      <div
        ref={ref}
        className={`post__caption ${expanded ? 'is-expanded' : ''} ${canExpand ? 'is-clamped' : ''}`}
      >
        <RichTextView html={html} className="post__body" />
      </div>
      {canExpand && (
        <button
          type="button"
          className="post__caption-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
}

// Bare icon (no pill outline), Instagram-style: the heart fills (in the accent)
// when liked, with the count beside it.
function LikeButton({ n, liked, onToggle }: { n: number; liked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="post__icon-btn"
      onClick={onToggle}
      aria-pressed={liked}
      aria-label="Like this post"
    >
      <HeartIcon on={liked} size={24} />
      {n > 0 && <span className="post__icon-count">{n}</span>}
    </button>
  );
}

// Bare share icon (no pill outline). A transient "Copied" shows on the
// clipboard fallback (when the native share sheet isn't available).
function ShareButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  async function share() {
    const url = `${window.location.origin}/p/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: 'CONVERTED CLOSET' });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    } catch { /* cancelled */ }
  }
  return (
    <button
      type="button"
      className="post__icon-btn"
      onClick={share}
      aria-label="Share this post"
      title={copied ? 'Link copied' : 'Share'}
    >
      <ShareIcon size={22} />
      {copied && <span className="post__icon-count">Copied</span>}
    </button>
  );
}

/** Paper-plane share glyph - replaces the old "Share" word in the comment row. */
function ShareIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 3L10.5 13.5M21 3l-6.8 18-3.7-8.5L2 9l19-6z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Bin glyph - the "delete your comment" affordance (own comments only). */
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7M10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function dollars(cents: number): string {
  // Always two decimals ("$40.00") - prices read as money, and the receipt
  // line is exactly what the card is charged.
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Auction-flavoured relative time. Short countdown for events within 24h
 * ("in 1h 23m" / "12m ago"), falls back to "Fri 6:00 PM" for longer
 * horizons. Used by AuctionBlock's state pill + bid history.
 */
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMs = t - Date.now();
  const absMs = Math.abs(diffMs);
  const future = diffMs > 0;

  if (absMs < 24 * 60 * 60 * 1000) {
    const totalMin = Math.floor(absMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const stamp = h > 0 ? `${h}h ${m}m` : `${m}m`;
    return future ? `in ${stamp}` : `${stamp} ago`;
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

/**
 * Map raw RPC exception strings from place_bid / buy_now into member-facing
 * copy. The Postgres exceptions are stable (see migration 031 - the `raise
 * exception '<name>'` calls) so a substring match is robust enough.
 */
function mapBidError(err: any): string {
  const msg = String(err?.message ?? '');
  if (msg.includes('auction_not_started')) return "Bidding hasn't opened yet.";
  if (msg.includes('auction_ended'))       return 'Bidding has closed.';
  if (msg.includes('buy_now_unavailable')) return 'Buy now is no longer available on this lot.';
  if (msg.includes('no_buy_now'))          return 'Buy now is no longer available on this lot.';
  if (msg.includes('bid_too_low'))         return 'Your bid must beat the current bid by at least $1.';
  if (msg.includes('below_min_bid'))       return "Your bid is below this lot's minimum.";
  if (msg.includes('self_bid'))            return "You can't bid on your own lot.";
  if (msg.includes('no_card_on_file'))     return 'Add a payment card before bidding.';
  if (msg.includes('no_access'))           return 'Members only. Sign in to bid.';
  if (msg.includes('not_authenticated'))   return 'Sign in to bid.';
  return 'Something broke placing that bid. Try again.';
}

// Instagram-style compact age: a single unit, no "ago" - "now", "5m", "3h",
// "4d", "7w", "2y". The whole point of the comment redesign is that the timestamp
// reads as a quiet footnote, not a full date sitting next to the comment.
function formatRelative(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${Math.max(1, mo)}mo`;
  return `${Math.floor(d / 365)}y`;
}
